/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { JSXInternal } from 'preact/src/jsx';
import { styles } from './themes/AxisStyles.css';
import { classNames } from '#utils/UNSAFE_classNames';
import { TextProps } from '#PRIVATE_VisSVGText/text.types';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { MultilineText, SinglelineText } from '#PRIVATE_VisSVGText';

type SVGTextAttrs = JSXInternal.HTMLAttributes<SVGTextElement>;

type ChartText = {
  id?: string;
  isTitle: boolean;
  textProps: TextProps;
  isRotated: boolean;
  isRtl: boolean;
  isInteractive: boolean;
  style?: SVGTextAttrs['style'];
  dataProps?: Record<`data-oj-private-${string}`, string | undefined>;
  axisPosition?: 'left' | 'right' | 'top' | 'bottom';
};

/**
 * Renders the svg text with given style and svg attributes.
 */
export function ChartAxisText({
  style,
  textProps,
  isTitle,
  isInteractive,
  id,
  isRotated,
  isRtl,
  dataProps,
  axisPosition
}: ChartText) {
  const browser = getClientHints().browser;
  const className = classNames([
    isInteractive ? styles.isInteractive : '',
    isTitle ? styles.axisTitle : '',
    isTitle && isRotated ? styles.rotateAroundCenter : '',
    !isTitle && browser === 'safari' ? styles.middleBaseline : '',
    !isTitle || !isRotated ? styles.centralBaseline : '',
    isTitle && (axisPosition === 'left' || axisPosition === 'right')
      ? styles.textAfterEdgeBaseline
      : ''
  ]);

  const isMultiline = textProps.lines.length > 1;

  return isMultiline ? (
    <MultilineText
      class={className}
      text={textProps.text}
      dataProps={dataProps}
      isRtl={isRtl}
      isRotated={isRotated}
      lines={textProps.lines}
      lineProps={textProps.lineProps}
      textStyle={style}
      isTruncated={textProps.isTruncated}
      id={id}
    />
  ) : (
    <SinglelineText
      style={style}
      isRtl={isRtl}
      isRotated={isRotated}
      dataProps={dataProps}
      text={textProps.lines[0]}
      props={textProps.lineProps[0]}
      isTruncated={textProps.isTruncated}
      id={id}
      class={className}
    />
  );
}
